import { AspectRatio, Loading, cn } from "@mozioinc/ui";
import { useTranslation } from "react-i18next";
import { AirportTransferIcon } from "./airport-transfer-icon";
import { ReactComponent as AirportSvg } from "./assets/airport.svg";
import { ReactComponent as CalendarSvg } from "./assets/calendar.svg";
import { ReactComponent as ParkingSvg } from "./assets/parking-placeholder.svg";
import { ReactComponent as SmallArrowSvg } from "./assets/small-arrow.svg";
import { ReactComponent as TimeSvg } from "./assets/time.svg";
import { ParkingFeatureIcon } from "./parking-feature-icon";
import { PaymentDetails } from "./payment-details";
import type { PaymentDetailsProps } from "./payment-details";
import type { TransNamespace } from "../types";
import type { ParkingSearchResult } from "@mozioinc/query";

type PurchaseSummaryProps = {
  name: string;
  logoUrl?: string;
  location: string;
  arrivalDateTime: Date;
  departureDateTime: Date;
  airportTransferSummary?: ParkingSearchResult["airportTransferSummary"];
  features?: ParkingSearchResult["features"];
  price?: string;
  showPrice?: boolean;
  className?: string;
  paymentDetails?: PaymentDetailsProps;
};

const PurchaseSummary = ({
  arrivalDateTime,
  departureDateTime,
  location,
  price,
  showPrice = true,
  className,
  paymentDetails,
  logoUrl,
  name,
  airportTransferSummary,
  features,
}: PurchaseSummaryProps) => {
  const { t } = useTranslation<TransNamespace>("parking:purchase-summary");

  return (
    <div className={cn("flex flex-wrap gap-x-4 p-3", className)}>
      <div className="w-14">
        {logoUrl ? (
          <AspectRatio ratio={1 / 1}>
            <img
              src={logoUrl}
              alt={t("{{name}}'s logo", { name })}
              className="h-full w-full rounded-md object-cover"
            />
          </AspectRatio>
        ) : (
          <ParkingSvg className="text-6xl text-neutral-700" />
        )}
      </div>

      <div className="flex-1">
        <div className="flex items-center justify-between gap-2 text-xl font-semibold">
          <p className="inline">{name}</p>
          {showPrice && <span>{price ?? <Loading />}</span>}
        </div>
        <ul className="mt-2 text-sm leading-6 sm:flex sm:flex-wrap sm:gap-x-4 sm:gap-y-1">
          {airportTransferSummary && (
            <li className="flex items-center gap-1">
              <AirportTransferIcon
                className="text-xl"
                type={airportTransferSummary.type}
              />
              {airportTransferSummary.name}
            </li>
          )}
          {features?.map((feature) => (
            <li key={feature.id} className="flex items-center gap-1">
              <ParkingFeatureIcon className="text-xl" type={feature.type} />
              {feature.name}
            </li>
          ))}
        </ul>
      </div>

      <div
        className={cn(
          "mt-2 flex basis-full items-center justify-between border-t border-t-white pt-2 sm:mt-4 sm:items-start sm:border-none sm:pl-[4.375rem]",
          {
            "pt-0 sm:mt-0": !features,
          },
        )}
      >
        <div className="flex flex-wrap gap-1 gap-x-1 sm:flex-col sm:gap-y-2">
          <p className="flex basis-full items-center gap-1 text-sm font-semibold sm:font-normal">
            <AirportSvg className="hidden w-6 text-xl sm:block" />
            {location}
          </p>
          <DateTime date={arrivalDateTime} />
          <SmallArrowSvg className="h-auto text-xs sm:hidden" />
          <DateTime date={departureDateTime} />
        </div>
      </div>
      {paymentDetails && (
        <div className="mt-5 w-full">
          <PaymentDetails
            totalPrice={paymentDetails.totalPrice}
            finalPrice={paymentDetails.finalPrice}
            coupon={paymentDetails.coupon}
            currency={paymentDetails.currency}
          />
        </div>
      )}
    </div>
  );
};

type DateTimeProps = {
  date: Date;
};

const DateTime = ({ date }: DateTimeProps) => {
  const { t } = useTranslation("parking:purchase-summary");

  return (
    <span className="flex items-center text-xs sm:gap-1 sm:text-sm">
      <CalendarSvg className="hidden w-6 text-xl sm:block" />
      {t("{{val, datetime}}", {
        val: date,
        formatParams: {
          val: {
            year: "numeric",
            month: "short",
            day: "numeric",
          },
        },
      })}
      <span className="mr-0.5 sm:hidden">, </span>
      <TimeSvg className="hidden w-6 text-xl sm:block" />
      {t("{{val, datetime}}", {
        val: date,
        formatParams: {
          val: {
            hour: "numeric",
            minute: "numeric",
            hour12: true,
          },
        },
      })}
    </span>
  );
};

export { PurchaseSummary };
